import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewChecked, OnChanges, OnInit } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	HostBinding,
	inject,
	Input,
	input,
	model,
	output,
	PLATFORM_ID,
	ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import type { DropdownPosition } from '@ng-select/ng-select';
import { NgSelectModule } from '@ng-select/ng-select';
import type { Observable } from 'rxjs';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	filter,
	map,
	of,
	ReplaySubject,
	switchMap,
} from 'rxjs';

import { BaseDirective } from '@valk-nx/core/lib/directives/base/base.directive';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

import type { DropdownOption } from './dropdown.interface';
import { equalDropDownOptions } from './dropdown-options';

type VariantType = 'attached' | 'integrated' | 'detached';
type AlignmentType = 'left' | 'center' | 'right';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	exportAs: 'vpDropdown',
	host: { ngSkipHydration: 'true' },
	selector: `vp-dropdown`,
	styleUrls: ['./scss/dropdown.scss'],
	templateUrl: './dropdown.html',
	imports: [
		SVG_ICON_IMPORTS,
		CommonModule,
		NgSelectModule,
		FormsModule,
		ReactiveFormsModule,
	],
})
export class DropdownComponent
	extends BaseDirective
	implements OnInit, OnChanges, AfterViewChecked
{
	private readonly platformId = inject(PLATFORM_ID);

	className = input<string>('');
	hasError = input<boolean>(false);
	isValid = input<boolean>(false);
	isRequired = input<boolean>(false);
	alignment = input<AlignmentType>('left');
	selectData = input.required<DropdownOption[]>();
	variant = input<VariantType>('attached');
	inputId = input<string>('');
	label = input<string>('');
	control = input(new FormControl());
	searchable = input<boolean>(false);
	useAutoComplete = input<boolean>(true);
	dropdownPosition = input<DropdownPosition>('bottom');
	isWhite = input<boolean>(false);
	useAsFormControl = input<boolean>(false);
	downIcon = input<string>('icon-chevron-down');
	upIcon = input<string>('icon-chevron-up');
	validationClass = computed(() => {
		if (this.hasError()) return 'error';
		if (this.isValid() && this.isRequired()) return 'valid';
		return '';
	});

	showValidationIcon = computed(() => this.isValid() || this.hasError());
	_placeholder$ = new BehaviorSubject<string>(
		'components.dropdown.placeholder',
	);
	@Input() set placeholder(value: string) {
		this._placeholder$.next(value);
	}

	value = model.required<string>();

	selectedValue = output<string>();

	translatedPlaceholder$!: Observable<string>;
	private readonly selectData$ = new ReplaySubject<DropdownOption[]>();
	selectOptions$ = this.selectData$.pipe(
		distinctUntilChanged(equalDropDownOptions),
		filter(
			(options) =>
				!!options &&
				options.some((option) => option.label !== undefined),
		),
		switchMap((options: DropdownOption[]) => {
			/* v8 ignore start */
			if (options.length === 0) {
				return of([]);
			}
			/* v8 ignore stop */
			const optionLabels = options.map((option) => option.label);
			const translatedLabels$ = this.translate.stream(optionLabels).pipe(
				map((translations) => {
					return options.map((option) => {
						return {
							...option,
							label: translations[option.label],
						};
					});
				}),
			);
			const selectedLabels = options
				.map((option) => option.selectLabel)
				.filter((value) => value !== undefined);
			const translatedSelectLabel$ =
				selectedLabels.length > 0
					? this.translate.stream(selectedLabels as string[]).pipe(
							map((translations) => {
								return options.map((option) => {
									return {
										...option,
										selectLabel:
											translations[
												option.selectLabel as string
											],
									};
								});
							}),
						)
					: of(options);

			return combineLatest([
				translatedLabels$,
				translatedSelectLabel$,
			]).pipe(
				map(([labels, selectLabels]) => {
					return options.map((option, index) => {
						return {
							...option,
							label: labels[index].label,
							selectLabel: selectLabels[index]?.selectLabel,
						};
					});
				}),
			);
		}),
	);
	selectOptions = toSignal(this.selectOptions$, { initialValue: [] });
	selectedDropdownPlaceholder = computed(() => {
		const selectedValue = this.selectOptions().find(
			(option) => option.value === this.value(),
		)?.label;
		return selectedValue ?? this.value();
	});

	@HostBinding('style')
	get hostStyle(): Record<string, string> {
		const down = `url('https://assets.vandervalkonline.com/live/icons/${this.downIcon()}.svg')`;
		const up = `url('https://assets.vandervalkonline.com/live/icons/${this.upIcon()}.svg')`;
		return {
			'--down-icon': down,
			'--up-icon': up,
		};
	}

	constructor() {
		super();
	}

	ngOnInit(): void {
		this.translatedPlaceholder$ = this._placeholder$.pipe(
			switchMap((placeholder) => {
				return placeholder
					? this.translate.stream(placeholder)
					: of('');
			}),
		);
	}

	ngAfterViewChecked() {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.useAutoComplete() &&
			document.getElementById(this.inputId())
		) {
			// Ng-select doesnt allow to change autocomplete property on the input
			document
				.getElementById(this.inputId())!
				.setAttribute('autocomplete', 'not-autocomplete');
		}
	}

	ngOnChanges(): void {
		this.selectData$.next(this.selectData());
	}

	update() {
		this.selectedValue.emit(this.value());
	}
}
